import { isAllowedFetchUrl } from '@shared/utils/httpsApiUrl';

export interface ResilientFetchOptions {
  timeoutMs?: number;
  retries?: number;
  backoffMs?: number;
}

const DEFAULT_TIMEOUT_MS = 15_000;
const DEFAULT_RETRIES = 0;
const DEFAULT_BACKOFF_MS = 400;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** AbortSignal compatible Electron (AbortSignal.timeout parfois absent en renderer). */
function abortSignalAfter(ms: number): AbortSignal {
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
    return AbortSignal.timeout(ms);
  }
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

/** Fetch avec timeout, annulation et retry limité (backoff linéaire). */
export async function resilientFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
  opts: ResilientFetchOptions = {},
): Promise<Response> {
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const retries = opts.retries ?? DEFAULT_RETRIES;
  const backoffMs = opts.backoffMs ?? DEFAULT_BACKOFF_MS;

  const href =
    typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.href
        : input instanceof Request
          ? input.url
          : String(input);
  if (!isAllowedFetchUrl(href)) {
    throw new Error('Communication refusée : URL non HTTPS');
  }

  let lastError: unknown;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const res = await fetch(input, {
        ...init,
        signal: init.signal ?? abortSignalAfter(timeoutMs),
      });
      return res;
    } catch (err) {
      lastError = err;
      if (attempt < retries) await sleep(backoffMs * (attempt + 1));
    }
  }
  throw lastError;
}
